import { useCallback } from "react";
import ContentCopy from "@mui/icons-material/ContentCopy";
import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import json_view_pkg from "@uiw/react-json-view";
import { v4 } from "uuid";

import { dayjs } from "@thalamos/common";
import type {
  CreateSingleUseActionTokenRequest,
  SingleUseActionTokenUnbranded,
} from "@thalamos/aspire-common";
import { SINGLE_USE_ACTION_TOKEN_MAX_AGE_MONTHS } from "@thalamos/aspire-common";

import { CreateTokenDialog } from "./CreateTokenDialog.js";
import { api, apiHooks } from "~/api.js";
import {
  Button,
  ButtonIcon,
  renderErrorToast,
  renderSuccessToast,
} from "~/components/design-system/index.js";
import { LoadingSpinner } from "~/components/design-system/LoadingSpinner.js";
import { useDialogs } from "~/hooks/dialogs/index.js";
import { routeFns } from "~/routePaths.js";

const JsonView = json_view_pkg as unknown as typeof json_view_pkg.default;

export const SingleUseActionTokenPage = () => {
  const [{ data, loading }, refetchSingleUseActionTokens] =
    apiHooks.singleUseActionToken.getAll();

  const onCreateToken = useCallback(
    (
      type: CreateSingleUseActionTokenRequest["type"],
      context: CreateSingleUseActionTokenRequest["context"],
    ) => {
      const runAsync = async () => {
        const tokenId = v4();

        const response = await api.singleUseActionToken.create(tokenId, {
          type,
          context,
        } as CreateSingleUseActionTokenRequest);

        if (response.status === 204) {
          renderSuccessToast({ message: "Token created successfully" });
          await refetchSingleUseActionTokens();
        } else {
          const reason = (response.data as { message?: string } | undefined)
            ?.message;
          renderErrorToast({
            message: reason
              ? `Failed to create token: ${reason}`
              : "Failed to create token",
          });
        }
      };

      void runAsync();
    },
    [refetchSingleUseActionTokens],
  );

  const onRevokeToken = useCallback(
    async (id: string) => {
      const result = await api.singleUseActionToken.revoke(id);
      if (result.status === 204) {
        renderSuccessToast({ message: "Token revoked successfully" });
        await refetchSingleUseActionTokens();
      } else {
        const reason = (result.data as { message?: string } | undefined)
          ?.message;
        renderErrorToast({
          message: reason
            ? `Failed to revoke token: ${reason}`
            : "Failed to revoke token",
        });
      }
    },
    [refetchSingleUseActionTokens],
  );

  return (
    <SingleUseActionTokenPageInner
      status={loading ? "loading" : "loaded"}
      tokens={(loading ? [] : data?.tokens) ?? []}
      onCreateToken={onCreateToken}
      onRevokeToken={onRevokeToken}
    />
  );
};

export type SingleUseActionTokenPageInnerProps =
  | { status: "loading" }
  | {
      status: "loaded";
      tokens: Array<SingleUseActionTokenUnbranded>;
      onCreateToken: (
        type: CreateSingleUseActionTokenRequest["type"],
        context: CreateSingleUseActionTokenRequest["context"],
      ) => void;
      onRevokeToken: (tokenId: string) => Promise<void>;
    };

export const SingleUseActionTokenPageInner = (
  props: SingleUseActionTokenPageInnerProps,
) => {
  return props.status === "loading" ? (
    <SingleUseActionTokenPageLoading />
  ) : (
    <SingleUseActionTokenPageLoaded {...props} />
  );
};

const SingleUseActionTokenPageLoading = () => {
  return <LoadingSpinner />;
};

const SingleUseActionTokenPageLoaded = ({
  tokens,
  onCreateToken,
  onRevokeToken,
}: Extract<SingleUseActionTokenPageInnerProps, { status: "loaded" }>) => {
  const dialogs = useDialogs();

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          width: "100%",
          justifyContent: "space-between",
          marginTop: "1em",
          marginBottom: "1em",
        }}
      >
        <Typography variant="h4">Single Use Action Tokens</Typography>
        <Button
          endIcon={ButtonIcon.add}
          label="Create"
          onClick={() => {
            const runAsync = async () => {
              const result = await dialogs.open(CreateTokenDialog);
              if (result.continue) {
                onCreateToken(result.type, result.context);
              }
            };
            void runAsync();
          }}
        />
      </Box>
      <TableContainer>
        <Table size="small" aria-label="a dense table">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>ID</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Created At</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Context</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tokens.map(({ id, created, status, type, context }) => {
              // TODO: Do we want to manage expiration on the frontend,
              // or make it part of every token context?
              const isExpired =
                dayjs().diff(dayjs(created), "month") >=
                SINGLE_USE_ACTION_TOKEN_MAX_AGE_MONTHS;
              const contextId =
                "teamId" in context ? context.teamId : context.organisationId;
              const link = `${window.location.origin}${routeFns.useSingleUseActionToken(type, id, contextId)}`;
              return (
                <TableRow key={id}>
                  <TableCell>
                    {id}
                    {status === "pending" && !isExpired && (
                      <IconButton
                        onClick={() => {
                          const doAsync = async () => {
                            try {
                              await navigator.clipboard.writeText(link);
                              renderSuccessToast({
                                message: "Copied to clipboard",
                              });
                            } catch {
                              renderErrorToast({
                                message: "Failed to copy to clipboard",
                              });
                            }
                          };
                          void doAsync();
                        }}
                      >
                        <ContentCopy />
                      </IconButton>
                    )}
                  </TableCell>
                  <TableCell>{isExpired ? "expired" : status}</TableCell>
                  <TableCell>
                    {dayjs(created).format("DD/MM/YYYY HH:mm:ss")}
                  </TableCell>
                  <TableCell>{type}</TableCell>
                  <TableCell>
                    <JsonView
                      value={context}
                      displayObjectSize={false}
                      displayDataTypes={false}
                      shortenTextAfterLength={100}
                      enableClipboard={false}
                    />
                  </TableCell>
                  <TableCell>
                    <Button
                      label="Revoke"
                      color="error"
                      disabled={status !== "pending"}
                      onClick={() => {
                        const runAsync = async () => {
                          const result = await dialogs.confirm(
                            "Are you sure you want to revoke this token?",
                          );
                          if (result) {
                            await onRevokeToken(id);
                          }
                        };

                        void runAsync();
                      }}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
